import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Input,
  Select,
  Button,
  Card,
  Avatar,
  Tag,
  Modal,
  Row,
  Col,
  Space,
  Spin,
  message,
  Pagination,
  Switch,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  TeamOutlined,
  CheckCircleFilled,
  DownloadOutlined,
} from "@ant-design/icons";
import { PageHeader, FindTalentsWrapper } from "./FindTalents.style";
import axios from "axios";
import { API_CONST } from "../../../const";
import ScheduleInterviewModal from "../../../components/ScheduleInterviewModal/ScheduleInterviewModal";

const { Option } = Select;

const FindTalents = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [filterValues, setFilterValues] = useState({
    skills: [],
  });
  const [primarySkillFilter, setPrimarySkillFilter] = useState(null);
  const [scheduleModalTalent, setScheduleModalTalent] = useState(null);
  const [resumeLoadingId, setResumeLoadingId] = useState(null);
  const [jobMatchCounts, setJobMatchCounts] = useState({});

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });

  const [filterOptions, setFilterOptions] = useState({
    roles: [],
    locations: [],
    primarySkills: [],
  });
  const [myPartnersOnly, setMyPartnersOnly] = useState(
    () => sessionStorage.getItem("findTalents_myPartnersOnly") === "true"
  );
  const [activePartnerOrgs, setActivePartnerOrgs] = useState([]);
  const [partnerOrgsLoading, setPartnerOrgsLoading] = useState(false);

  useEffect(() => {
    const roleParam = searchParams.get("role") || searchParams.get("search");
    if (roleParam) {
      setSearchText(roleParam);
    }
  }, []); // run once on mount to read URL search params

  const fetchActivePartnerOrgs = useCallback(async () => {
    setPartnerOrgsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.PARTNER_RELATION_MY_PARTNERS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const relations = response.data.relations || [];
      const orgs = relations
        .filter((r) => r.status === "active" && r.partnerOrganization)
        .map((r) => r.partnerOrganization);
      setActivePartnerOrgs(orgs);
    } catch (error) {
      console.error("Error fetching partner orgs:", error);
      message.error("Failed to load partner organizations");
    } finally {
      setPartnerOrgsLoading(false);
    }
  }, []);

  const handleMyPartnersToggle = (checked) => {
    setMyPartnersOnly(checked);
    sessionStorage.setItem("findTalents_myPartnersOnly", String(checked));
    if (checked && activePartnerOrgs.length === 0) {
      fetchActivePartnerOrgs();
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    if (myPartnersOnly && activePartnerOrgs.length === 0) {
      fetchActivePartnerOrgs();
    }
  }, [myPartnersOnly, activePartnerOrgs.length, fetchActivePartnerOrgs]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TALENT_PROFILES,
        { pageSize: 1000, status: 'Active' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const allTalents = response.data.Response || [];
      
      const roles = [...new Set(allTalents.map(t => t.role).filter(Boolean))];
      const locations = [...new Set(allTalents.map(t => t.location).filter(Boolean))];
      const allPrimarySkills = allTalents.flatMap(t => t.primarySkills || []).filter(Boolean);
      const uniquePrimarySkills = [...new Set(allPrimarySkills)];
      
      setFilterOptions({
        roles: roles.sort(),
        locations: locations.sort(),
        primarySkills: uniquePrimarySkills.sort(),
      });
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, []);

  const fetchTalents = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      
      const hasClientSideFilters = primarySkillFilter || (filterValues.skills && filterValues.skills.length > 0);

      if (myPartnersOnly && activePartnerOrgs.length === 0) {
        setTalents([]);
        setPagination(prev => ({ ...prev, total: 0 }));
        return;
      }
      
      const requestBody = {
        status: 'Active',
      };
      
      const needsClientSideFiltering = hasClientSideFilters;
      if (!needsClientSideFiltering) {
        requestBody.page = pagination.current;
        requestBody.pageSize = pagination.pageSize;
      } else {
        requestBody.pageSize = 1000;
      }
      
      if (searchText) {
        requestBody.search = searchText;
      }
      if (roleFilter) {
        requestBody.role = roleFilter;
      }
      if (locationFilter) {
        requestBody.location = locationFilter;
      }
      if (myPartnersOnly && activePartnerOrgs.length > 0) {
        requestBody.organization = activePartnerOrgs;
      }
      
      const response = await axios.post(
        API_CONST.GET_TALENT_PROFILES,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let talentData = response.data.Response || [];
      
      if (primarySkillFilter) {
        talentData = talentData.filter(talent => {
          const talentPrimarySkills = (talent.primarySkills || []).map(s => s?.toLowerCase());
          return talentPrimarySkills.some(ps => ps?.includes(primarySkillFilter.toLowerCase()));
        });
      }
      
      if (filterValues.skills && filterValues.skills.length > 0) {
        talentData = talentData.filter(talent => {
          const secondarySkills = (talent.secondarySkills || []).map(s => s?.toLowerCase());
          return filterValues.skills.some(skill => 
            secondarySkills.some(ts => ts?.includes(skill.toLowerCase()))
          );
        });
      }

      if (needsClientSideFiltering) {
        const totalFiltered = talentData.length;
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        talentData = talentData.slice(startIndex, endIndex);
        
        setTalents(talentData);
        setPagination(prev => ({
          ...prev,
          total: totalFiltered,
        }));
      } else {
        setTalents(talentData);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || talentData.length,
        }));
      }
    } catch (error) {
      console.error("Error fetching talents:", error);
      message.error("Failed to load talents");
    } finally {
      setLoading(false);
    }
  }, [searchText, roleFilter, locationFilter, primarySkillFilter, filterValues, pagination.current, pagination.pageSize, myPartnersOnly, activePartnerOrgs]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchTalents();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [fetchTalents]);

  // Fetch job-match counts for visible talents (Customer only)
  useEffect(() => {
    if (!talents.length) return;
    const token = localStorage.getItem("authToken");
    const fetchCounts = async () => {
      const counts = {};
      await Promise.all(
        talents.map(async (t) => {
          try {
            const res = await axios.get(
              `${API_CONST.TALENT_JOB_MATCHES}/${t.id}/job-matches`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            counts[t.id] = res.data.matchCount || 0;
          } catch (e) {
            counts[t.id] = 0;
          }
        })
      );
      setJobMatchCounts(counts);
    };
    fetchCounts();
  }, [talents]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRoleChange = (value) => {
    setRoleFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleLocationChange = (value) => {
    setLocationFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetails = (talent) => {
    navigate(`/customer/talent-details/${talent.id}`);
  };

  const handleScheduleInterview = (talent) => {
    setScheduleModalTalent(talent);
  };

  const handleDownloadBrandedResume = async (talent) => {
    setResumeLoadingId(talent.id);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.BRANDED_RESUME_BASE}/${talent.id}/branded-resume`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank");
      } else {
        message.error("Could not get resume download URL");
      }
    } catch (error) {
      console.error("Error downloading branded resume:", error);
      message.error("Failed to download resume. Please try again.");
    } finally {
      setResumeLoadingId(null);
    }
  };

  const showFilterModal = () => {
    setIsFilterModalVisible(true);
  };

  const handleFilterApply = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setIsFilterModalVisible(false);
  };

  const handleFilterCancel = () => {
    setIsFilterModalVisible(false);
  };

  const handlePrimarySkillChange = (value) => {
    setPrimarySkillFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setSearchText("");
    setRoleFilter(null);
    setLocationFilter(null);
    setPrimarySkillFilter(null);
    setFilterValues({ skills: [] });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
  };

  const formatRate = (rate) => {
    if (!rate) return '-';
    return `₹ ${Number(rate).toLocaleString('en-IN')}`;
  };

  const formatExperience = (years, months) => {
    const y = years || 0;
    const m = months || 0;
    if (y === 0 && m === 0) return 'Fresher';
    if (y === 0) return `${m} Months`;
    if (m === 0) return `${y} Years`;
    return `${y} Years ${m} Months`;
  };

  const getPrimarySkillsForDisplay = (primarySkills) => {
    if (!primarySkills || !Array.isArray(primarySkills)) return [];
    return primarySkills.slice(0, 4).filter(Boolean);
  };

  const hasActiveFilters = searchText || roleFilter || locationFilter || primarySkillFilter ||
    filterValues.skills.length > 0;

  return (
    <FindTalentsWrapper>
      <PageHeader>
        <h1>Find Talents</h1>
        <p>Search and discover skilled technology professionals</p>
      </PageHeader>

      <div className="filter-section">
        <Input
          placeholder="Search by name or role"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
          allowClear
        />

        <Select
          placeholder="Role"
          style={{ width: 200 }}
          value={roleFilter}
          onChange={handleRoleChange}
          allowClear
        >
          {filterOptions.roles.map(role => (
            <Option key={role} value={role}>{role}</Option>
          ))}
        </Select>

        <Select
          placeholder="Location"
          style={{ width: 180 }}
          value={locationFilter}
          onChange={handleLocationChange}
          allowClear
        >
          {filterOptions.locations.map(loc => (
            <Option key={loc} value={loc}>{loc}</Option>
          ))}
        </Select>

        <Select
          placeholder="Primary Skill"
          style={{ width: 200 }}
          value={primarySkillFilter}
          onChange={handlePrimarySkillChange}
          allowClear
          showSearch
        >
          {filterOptions.primarySkills.map(skill => (
            <Option key={skill} value={skill}>{skill}</Option>
          ))}
        </Select>

        <Button
          icon={<FilterOutlined />}
          onClick={showFilterModal}
          className="all-filters-btn"
        >
          More Filters
        </Button>

        {hasActiveFilters && (
          <Button onClick={handleClearFilters} type="link" danger>
            Clear Filters
          </Button>
        )}

        <Tooltip title="Show only talents from your registered partner network">
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: myPartnersOnly ? "#e6f7ff" : "#fafafa",
            border: `1px solid ${myPartnersOnly ? "#91d5ff" : "#d9d9d9"}`,
            borderRadius: 6,
            padding: "6px 12px",
            cursor: "pointer",
          }}>
            <TeamOutlined style={{ color: myPartnersOnly ? "#1890ff" : "#999" }} />
            <span style={{ fontSize: 13, color: myPartnersOnly ? "#1890ff" : "#595959", userSelect: "none" }}>
              My Partners Only
            </span>
            <Switch
              size="small"
              checked={myPartnersOnly}
              onChange={handleMyPartnersToggle}
              loading={partnerOrgsLoading}
            />
          </div>
        </Tooltip>
      </div>

      <Spin spinning={loading}>
        {talents.length === 0 && !loading ? (
          <Card style={{ textAlign: 'center', padding: 40 }}>
            <p style={{ color: '#8c8c8c' }}>No talents found matching your criteria</p>
          </Card>
        ) : (
          <>
            <Row gutter={[16, 16]} className="talents-grid">
              {talents.map((talent) => (
                <Col xs={24} sm={12} md={8} lg={6} key={talent.id}>
                  <Card className="talent-card" hoverable>
                    <div className="talent-header">
                      <Avatar size={48} className="talent-avatar">
                        {talent.name?.charAt(0) || '?'}
                      </Avatar>
                      <div className="talent-info">
                        <h4 className="talent-name">{talent.name}</h4>
                        <p className="talent-location">
                          <EnvironmentOutlined /> {talent.location || 'Not specified'}
                        </p>
                        {talent.idVerificationStatus === "Verified" && (
                          <Tag icon={<CheckCircleFilled />} color="success" style={{ fontSize: 11, marginTop: 2 }}>
                            ID Verified
                          </Tag>
                        )}
                        {talent.idVerificationStatus === "Failed" && (
                          <Tag color="error" style={{ fontSize: 11, marginTop: 2 }}>
                            ID Failed
                          </Tag>
                        )}
                      </div>
                    </div>

                    <div className="talent-details">
                      <p className="experience">
                        Experience: {formatExperience(talent.experienceYears, talent.experienceMonths)}
                      </p>
                      <h3 className="role">{talent.role}</h3>
                      <p className="rate-label">Monthly Rate</p>
                      <h3 className="rate">{formatRate(talent.monthlyRate || talent.clientRate || talent.rate)}</h3>
                    </div>

                    <div className="skills-section">
                      <p className="skills-label">Primary Skills</p>
                      <div className="skills-tags">
                        {getPrimarySkillsForDisplay(talent.primarySkills).map((skill, index) => (
                          <Tag key={index} color="cyan">
                            {skill}
                          </Tag>
                        ))}
                        {(talent.primarySkills?.length || 0) > 4 && (
                          <Tag>+{talent.primarySkills.length - 4}</Tag>
                        )}
                      </div>
                    </div>

                    {jobMatchCounts[talent.id] > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <Tag color="green" icon={<CheckCircleFilled />} style={{ fontSize: 12, fontWeight: 600 }}>
                          Matches {jobMatchCounts[talent.id]} job{jobMatchCounts[talent.id] > 1 ? "s" : ""}
                        </Tag>
                      </div>
                    )}
                    <div className="action-buttons">
                      <Button
                        type="primary"
                        block
                        onClick={() => handleViewDetails(talent)}
                        className="view-details-btn"
                      >
                        View Details
                      </Button>
                      <Button
                        block
                        onClick={() => handleScheduleInterview(talent)}
                        className="schedule-btn"
                      >
                        Schedule Interview
                      </Button>
                      <Button
                        block
                        icon={<DownloadOutlined />}
                        loading={resumeLoadingId === talent.id}
                        onClick={() => handleDownloadBrandedResume(talent)}
                        className="download-resume-btn"
                      >
                        Download HireNXT Resume
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {pagination.total > pagination.pageSize && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger
                  pageSizeOptions={['12', '24', '48']}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} talents`}
                />
              </div>
            )}
          </>
        )}
      </Spin>

      <Modal
        title="More Filters"
        open={isFilterModalVisible}
        onOk={handleFilterApply}
        onCancel={handleFilterCancel}
        okText="Apply Filters"
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <label>Secondary Skills</label>
            <Select
              mode="multiple"
              placeholder="Select skills to filter by"
              style={{ width: "100%", marginTop: 8 }}
              value={filterValues.skills}
              onChange={(values) =>
                setFilterValues({ ...filterValues, skills: values })
              }
            >
              <Option value="react">React</Option>
              <Option value="nodejs">Node.js</Option>
              <Option value="python">Python</Option>
              <Option value="java">Java</Option>
              <Option value="angular">Angular</Option>
              <Option value="php">PHP</Option>
              <Option value="mongodb">MongoDB</Option>
              <Option value="html">HTML</Option>
              <Option value="css">CSS</Option>
              <Option value="javascript">JavaScript</Option>
              <Option value="typescript">TypeScript</Option>
              <Option value="docker">Docker</Option>
              <Option value="kubernetes">Kubernetes</Option>
              <Option value="aws">AWS</Option>
              <Option value="spring">Spring</Option>
              <Option value="mysql">MySQL</Option>
              <Option value="postgresql">PostgreSQL</Option>
            </Select>
          </div>
        </Space>
      </Modal>

      <ScheduleInterviewModal
        visible={!!scheduleModalTalent}
        talentId={scheduleModalTalent?.id}
        talentName={scheduleModalTalent?.name}
        onClose={() => setScheduleModalTalent(null)}
      />
    </FindTalentsWrapper>
  );
};

export default FindTalents;
